import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";
import { formatCurrency } from "../utils/assetHelpers";
import { DEFAULT_ASSET_STATS } from "../utils/assetConstants";
import api from "../../../../api/api";
import { useSnackbar } from "notistack";
import InventoryHistoryEvents from '../../history/InventoryHistoryEvents';

const AssetStatistics = ({ assetId }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [assetStats, setAssetStats] = useState(DEFAULT_ASSET_STATS);
  const [rentalHistory, setRentalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const requestVersion = useRef(0);
  
  // Состояния для импорта/экспорта
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Состояния для привязки по номерам
  const [dealNumbers, setDealNumbers] = useState('');
  const [linking, setLinking] = useState(false);

  const fetchAssetStats = async () => {
    const version = ++requestVersion.current;
    if (!assetId) { setLoading(false); return; }
    
    try {
      setLoading(true);
      setLoadError('');
      const response = await api.get(`/assets/${assetId}/rental-history`);
      if (version !== requestVersion.current) return;
      
      if (response.data?.summary) {
        setAssetStats({
          total_rentals: response.data.summary.total_rentals || 0,
          active_rentals: response.data.summary.active_rentals || 0,
          completed_rentals: response.data.summary.completed_rentals || 0,
          total_revenue: response.data.summary.total_revenue || 0
        });
      } else setAssetStats(DEFAULT_ASSET_STATS);
      
      // Сохраняем историю аренды (последние 5 записей для предварительного просмотра)
      if (response.data?.deals) {
        setRentalHistory(response.data.deals.slice(0, 5));
      } else setRentalHistory([]);
    } catch (error) {
      if (version !== requestVersion.current) return;
      console.error('Ошибка загрузки статистики актива:', error);
      setLoadError('Ошибка загрузки истории аренды');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssetStats();
    return () => { requestVersion.current += 1; };
  }, [assetId]);

  // Экспорт истории сделок
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/assets/${assetId}/deals/export`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `asset_${assetId}_deals.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('История сделок экспортирована', { variant: 'success' });
    } catch (error) {
      console.error('Ошибка экспорта:', error);
      enqueueSnackbar('Ошибка экспорта истории сделок', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  // Скачивание шаблона для импорта
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/assets/${assetId}/deals/import/template`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_deals_asset_${assetId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('Шаблон загружен', { variant: 'success' });
    } catch (error) {
      console.error('Ошибка загрузки шаблона:', error);
      enqueueSnackbar('Ошибка загрузки шаблона', { variant: 'error' });
    }
  };

  // Импорт истории сделок
  const handleImport = async () => {
    if (!importFile) {
      enqueueSnackbar('Выберите файл для импорта', { variant: 'warning' });
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await api.post(`/assets/${assetId}/deals/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      enqueueSnackbar(
        `Импортировано: ${response.data.imported}, Обновлено: ${response.data.updated}, Пропущено: ${response.data.skipped}`,
        { variant: 'success' }
      );
      
      setImportDialog(false);
      setImportFile(null);
      
      // Обновляем статистику после импорта
      fetchAssetStats();
    } catch (error) {
      console.error('Ошибка импорта:', error);
      enqueueSnackbar(error.response?.data?.detail || 'Ошибка импорта сделок', { variant: 'error' });
    } finally {
      setImporting(false);
    }
  };

  // Привязка аренд по номерам
  const handleLinkDeals = async () => {
    if (!dealNumbers.trim()) {
      enqueueSnackbar('Введите номера аренд', { variant: 'warning' });
      return;
    }
    
    setLinking(true);
    try {
      const response = await api.post(`/assets/${assetId}/deals/link`, {
        deal_numbers: dealNumbers
      });
      
      let message = `Привязано: ${response.data.linked}`;
      if (response.data.already_linked > 0) {
        message += `, уже привязано: ${response.data.already_linked}`;
      }
      if (response.data.not_found?.length > 0) {
        message += `, не найдено: ${response.data.not_found.join(', ')}`;
      }
      
      enqueueSnackbar(message, { 
        variant: response.data.linked > 0 ? 'success' : 'warning' 
      });
      
      setDealNumbers('');
      fetchAssetStats();
    } catch (error) {
      console.error('Ошибка привязки:', error);
      enqueueSnackbar(error.response?.data?.detail || 'Ошибка привязки аренд', { variant: 'error' });
    } finally {
      setLinking(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'активная':
      case 'active':
        return 'success';
      case 'завершена':
      case 'completed':
        return 'default';
      case 'отменена':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      {loadError && <Alert severity="error">{loadError}</Alert>}
      {/* Краткая сводка */}
      <Card elevation={3} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">
              Краткая сводка аренды
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => setImportDialog(true)}
                size="small"
                color="primary"
              >
                Импорт
              </Button>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleExport}
                size="small"
                disabled={exporting || assetStats.total_rentals === 0}
              >
                Экспорт
              </Button>
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => navigate(`/assets/${assetId}/history`)}
                size="small"
              >
                Полная история
              </Button>
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {loading ? "..." : assetStats.total_rentals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Всего аренд
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {loading ? "..." : assetStats.active_rentals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Активных
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="text.primary">
                  {loading ? "..." : assetStats.completed_rentals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Завершенных
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {loading ? "..." : formatCurrency(assetStats.total_revenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Общая выручка
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Быстрая привязка аренд по номерам */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Привязать аренды по номерам
            </Typography>
            <Box display="flex" gap={1} alignItems="flex-start">
              <TextField
                size="small"
                placeholder="Например: 867, 839, 810"
                value={dealNumbers}
                onChange={(e) => setDealNumbers(e.target.value)}
                fullWidth
                helperText="Введите номера аренд через запятую"
              />
              <Button
                variant="contained"
                onClick={handleLinkDeals}
                disabled={linking || !dealNumbers.trim()}
                startIcon={linking ? <CircularProgress size={16} /> : <LinkIcon />}
                sx={{ minWidth: 120, height: 40 }}
              >
                {linking ? "..." : "Привязать"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Раскрываемые секции */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon color="action" />
            <Typography variant="h6">
              История аренды
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {loading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          ) : rentalHistory.length > 0 ? (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>№ аренды</TableCell>
                      <TableCell>Клиент</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Начало аренды</TableCell>
                      <TableCell>Окончание</TableCell>
                      <TableCell align="right">Сумма</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rentalHistory.map((deal, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {deal.deal_number || deal.id || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {deal.client ? (
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {deal.client.client_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {deal.client.contact_info}
                              </Typography>
                            </Box>
                          ) : 'Не указан'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={deal.status || 'Не указан'} 
                            color={getStatusColor(deal.status)}
                            size="small"
                          />
                          <InventoryHistoryEvents events={deal.inventory_events} />
                        </TableCell>
                        <TableCell>
                          {formatDate(deal.start_date)}
                        </TableCell>
                        <TableCell>
                          {formatDate(deal.end_date)}
                        </TableCell>
                        <TableCell align="right">
                          {deal.history_only ? '—' : formatCurrency(deal.total_amount || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              История аренды отсутствует
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUpIcon color="info" />
            <Typography variant="h6">
              Статистика использования
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {loading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Популярность
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {assetStats.total_rentals > 10 ? 'Высокая' : 
                     assetStats.total_rentals > 5 ? 'Средняя' : 'Низкая'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    На основе {assetStats.total_rentals} аренд
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Средняя выручка за аренду
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {assetStats.total_rentals > 0 
                      ? formatCurrency(assetStats.total_revenue / assetStats.total_rentals)
                      : formatCurrency(0)
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Общая выручка: {formatCurrency(assetStats.total_revenue)}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Коэффициент использования
                  </Typography>
                  <Typography variant="h6" color="info.main">
                    {assetStats.total_rentals > 0 ? 
                      `${Math.round((assetStats.completed_rentals / assetStats.total_rentals) * 100)}%`
                      : '0%'
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Завершенных аренд: {assetStats.completed_rentals}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Текущий статус
                  </Typography>
                  <Typography variant="h6" color={assetStats.active_rentals > 0 ? "warning.main" : "success.main"}>
                    {assetStats.active_rentals > 0 ? 'В аренде' : 'Доступен'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Активных аренд: {assetStats.active_rentals}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Диалог импорта сделок */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Импорт истории сделок для этого актива</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Все сделки из загруженного файла будут привязаны к <strong>этому активу</strong>. 
              Это удобно для переноса истории из другой системы.
            </Typography>
            
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleDownloadTemplate}
              sx={{ mb: 2 }}
            >
              Скачать шаблон Excel
            </Button>
          </Box>
          
          <Typography variant="caption" component="div" sx={{ mb: 2, color: 'text.secondary' }}>
            <strong>Обязательные поля:</strong> Клиент, Дата начала<br/>
            <strong>Дополнительные:</strong> Телефон, Дата окончания, Дата закрытия, Статус, 
            Итоговая стоимость, Залог, Оплачено
          </Typography>
          
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
          >
            Выбрать файл
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setImportFile(e.target.files[0])}
            />
          </Button>
          
          {importFile && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Выбран файл: {importFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)} disabled={importing}>
            Отмена
          </Button>
          <Button 
            onClick={handleImport} 
            variant="contained" 
            disabled={!importFile || importing}
          >
            {importing ? "Импорт..." : "Импортировать"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetStatistics;
